<script setup lang="ts" name="AdminScheduleRecipientListNewPreview">
import { reactive, ref } from 'vue';

import {
  getCategoricalColors,
  getCategoricalTagColors,
} from '~/features/useColors';
import { useLastUpdated } from '~/features/useLastSync';
import { useOnResize } from '~/features/useOnResize';
import { useSailingScheduleTradeColoring } from '~/features/useSailingSchedule';
import {
  dateTypes,
  useAdminSailingScheduleDateType,
} from '~/features/useSailingScheduleFilters';

import Service from '~/services/Service';

import BaseAlert from '~/components/BaseAlert.vue';
import BaseCardNoResults from '~/components/BaseCardNoResults.vue';
import BasePage from '~/components/BasePage.vue';
import G2oPublicLayout from '~/components/G2oPublicLayout.vue';
import SailingScheduleContent from '~/components/SailingScheduleContent.vue';
import SailingScheduleNoDataCard from '~/components/SailingScheduleNoDataCard.vue';
import SailingScheduleTradeLaneCollapsible from '~/components/SailingScheduleTradeLaneCollapsible.vue';
import SailingScheduleVoyageCollapsible from '~/components/SailingScheduleVoyageCollapsible.vue';
import TextToggle from '~/components/TextToggle.vue';

import type { SailingScheduleTradeLane, SailingScheduleVoyages } from '~/types';

const props = defineProps<{
  tradelanes: string[] | string;
}>();

const { lastUpdated, setLastUpdated } = useLastUpdated();
const { isOnMobile } = useOnResize();
const { getTradeColor } = useSailingScheduleTradeColoring();

const { dateType, setSailingScheduleDateType } =
  useAdminSailingScheduleDateType();

const tradelanes = ref<SailingScheduleTradeLane[]>([]);
const voyageTradelanes = ref<SailingScheduleVoyages[]>([]);

const query = reactive({
  cacheFilters: false,
  tradelanes:
    typeof props.tradelanes === 'string'
      ? [{ id: props.tradelanes, name: props.tradelanes }]
      : props.tradelanes.map((trade) => ({
          id: trade,
          name: trade,
        })),
});
</script>

<template>
  <G2oPublicLayout headerLogo="g2o">
    <BasePage title="Sailing Schedule" :last-updated="lastUpdated">
      <BaseAlert type="info" class="col-span-full rounded drop-shadow">
        All dates, vessels, and port calls are subject to change without notice
      </BaseAlert>

      <div
        v-if="tradelanes.length > 0 || voyageTradelanes.length > 0"
        class="col-span-full hidden rounded-md bg-white p-4 shadow sm:flex sm:justify-end"
      >
        <TextToggle
          :options="dateTypes"
          :modelValue="dateType"
          @update:model-value="(value) => setSailingScheduleDateType(value)"
        />
      </div>

      <div class="col-span-full space-y-2">
        <SailingScheduleContent
          v-if="isOnMobile"
          paginated
          v-model:items="voyageTradelanes"
          v-model:query="query"
          :service="
            Service.sailingSchedule()
              .indexMobile()
              .data({ tradelanes: props.tradelanes, cacheFilters: false })
          "
          @setLastUpdated="setLastUpdated"
        >
          <template #no-data>
            <BaseCardNoResults
              styled
              hideClear
              message="There are no voyages available for your selected trades at the moment."
            />
          </template>

          <template #default="{ items }">
            <SailingScheduleVoyageCollapsible
              v-for="voyage in items"
              :key="voyage.tradelane"
              :voyage="voyage"
              v-bind="getTradeColor(voyage.tradelane)"
            />
          </template>
        </SailingScheduleContent>

        <SailingScheduleContent
          v-else
          paginated
          v-model:items="tradelanes"
          v-model:query="query"
          :service="Service.sailingSchedule().index()"
          @setLastUpdated="setLastUpdated"
        >
          <template #no-data>
            <SailingScheduleNoDataCard />
          </template>

          <template #default="{ items }">
            <SailingScheduleTradeLaneCollapsible
              v-for="(tradeLane, index) in items"
              :key="tradeLane.tradelane"
              :voyageService="
                Service.sailingSchedule().voyages(tradeLane.tradelane).index()
              "
              :dateType="dateType"
              :trade-lane="tradeLane"
              :tagVariant="getCategoricalTagColors(index)"
              :color="getCategoricalColors(index)"
            />
          </template>
        </SailingScheduleContent>
      </div>
    </BasePage>
  </G2oPublicLayout>
</template>
